import { FlexCameraSummary, FlexPreset } from "./types";
import { normalizeFlexPresetSelectionToApiBackedMenuValue } from "./flexPresetDisplayDedupe";
import {
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_LABEL,
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION,
} from "./flexIzPresetSelectConstants";

export type FlexIzScheduleSummaryRow = {
  cameraId: number;
  camera: FlexCameraSummary;
  preset: FlexPreset;
};

/**
 * "Default" = no preset assigned for this camera under the schedule: the camera
 * runs its own (camera-level) ignore zones and default line crossings.
 */
export const defaultCameraConfigPreset = (camera: FlexCameraSummary): FlexPreset => ({
  localId: FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION,
  cameraId: camera.id,
  name: FLEX_IZ_DEFAULT_CAMERA_CONFIG_LABEL,
  zones: camera.ignore_zones || [],
  persistedId: null,
  source: "default",
  userNamed: false,
});

const serverPresetIdFromLocalId = (localId: string): number | null => {
  const match = /^server-(\d+)$/.exec(localId);
  if (!match) {
    return null;
  }
  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
};

/**
 * Resolve the preset row to show for a camera under a schedule (ENG-1610):
 * - no / empty / Default selection → "Default" (camera-wide zones and lines);
 * - a selection present in the camera's preset list → that preset;
 * - a `server-<id>` selection missing from the list (e.g. collapsed by
 *   display-name dedupe) → a placeholder carrying the real name from
 *   `GET preset/?camera=` (`presetNamesByCamera`), never `presets[0]`.
 */
const resolvePresetForCamera = (
  camera: FlexCameraSummary,
  resolvedScheduleId: number,
  selectedPresetByScheduleCamera: Record<string, string>,
  presetsByCamera: Record<number, FlexPreset[]>,
  presetNamesByCamera: Record<number, Record<number, string>>
): FlexPreset => {
  const presets = presetsByCamera[camera.id] || [];
  const rawPresetLocalId =
    selectedPresetByScheduleCamera[`${resolvedScheduleId}:${camera.id}`];
  if (
    rawPresetLocalId == null ||
    rawPresetLocalId === "" ||
    rawPresetLocalId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION
  ) {
    return defaultCameraConfigPreset(camera);
  }
  const presetLocalId = normalizeFlexPresetSelectionToApiBackedMenuValue(
    camera.id,
    presets,
    rawPresetLocalId
  );
  const found =
    presets.find((preset) => preset.localId === presetLocalId) ||
    presets.find((preset) => preset.localId === rawPresetLocalId);
  if (found) {
    return found;
  }
  const serverPresetId = serverPresetIdFromLocalId(rawPresetLocalId);
  if (serverPresetId != null) {
    const realName = presetNamesByCamera[camera.id]?.[serverPresetId];
    return {
      localId: rawPresetLocalId,
      cameraId: camera.id,
      name: realName ?? `Preset #${serverPresetId}`,
      zones: [],
      persistedId: serverPresetId,
      source: "custom",
      userNamed: true,
    };
  }
  // A stale local id (discarded draft, wizard `default-*` slot that was never
  // loaded) has nothing real to show; render Default rather than guess.
  return defaultCameraConfigPreset(camera);
};

export function buildFlexIzScheduleSummary(params: {
  showFlexIgnoreZones: boolean;
  flexScheduleActive: boolean;
  flexIgnoreZonesEnabled: boolean;
  siteId: number | null;
  siteInfoId: number;
  siteSchedules: Array<{ id?: number } | undefined>;
  schedulesIndex: number;
  wizardSchedules: Array<{ id: number; display_name?: string }>;
  selectedCameraIdsBySchedule: Record<number, number[]>;
  selectedPresetByScheduleCamera: Record<string, string>;
  cameras: FlexCameraSummary[];
  presetsByCamera: Record<number, FlexPreset[]>;
  presetNamesByCamera?: Record<number, Record<number, string>>;
}): { resolvedScheduleId: number; rows: FlexIzScheduleSummaryRow[] } | null {
  const {
    showFlexIgnoreZones,
    flexScheduleActive,
    flexIgnoreZonesEnabled,
    siteId,
    siteInfoId,
    siteSchedules,
    schedulesIndex,
    wizardSchedules,
    selectedCameraIdsBySchedule,
    selectedPresetByScheduleCamera,
    cameras,
    presetsByCamera,
    presetNamesByCamera = {},
  } = params;

  const storeHydratedForSite =
    siteId != null && siteId === siteInfoId && cameras.length > 0;

  if (
    !showFlexIgnoreZones ||
    !flexScheduleActive ||
    !flexIgnoreZonesEnabled ||
    !storeHydratedForSite
  ) {
    return null;
  }

  const siteSchedule = siteSchedules[schedulesIndex];
  const wizardSchedule = wizardSchedules[schedulesIndex];
  let resolvedScheduleId: number | undefined;

  if (siteSchedule?.id != null && Number.isFinite(Number(siteSchedule.id))) {
    resolvedScheduleId = Number(siteSchedule.id);
  }
  if (resolvedScheduleId == null && wizardSchedule?.id != null) {
    resolvedScheduleId = Number(wizardSchedule.id);
  }

  if (resolvedScheduleId == null) {
    return null;
  }

  // Flex schedules apply to every camera on the site. Union the live camera
  // list with any previously selected IDs so a camera added after onboarding
  // still appears under Flex Schedule Presets.
  const cameraIds: number[] = [];
  const seen = new Set<number>();
  for (const camera of cameras) {
    if (!Number.isFinite(camera.id) || seen.has(camera.id)) {
      continue;
    }
    seen.add(camera.id);
    cameraIds.push(camera.id);
  }
  for (const cameraId of selectedCameraIdsBySchedule[resolvedScheduleId] ?? []) {
    if (!Number.isFinite(cameraId) || seen.has(cameraId)) {
      continue;
    }
    seen.add(cameraId);
    cameraIds.push(cameraId);
  }

  if (cameraIds.length === 0) {
    return null;
  }

  const rows: FlexIzScheduleSummaryRow[] = [];

  for (const cameraId of cameraIds) {
    const camera = cameras.find((candidate) => candidate.id === cameraId);
    if (!camera) {
      continue;
    }
    rows.push({
      cameraId,
      camera,
      preset: resolvePresetForCamera(
        camera,
        resolvedScheduleId,
        selectedPresetByScheduleCamera,
        presetsByCamera,
        presetNamesByCamera
      ),
    });
  }

  if (rows.length === 0) {
    return null;
  }

  return { resolvedScheduleId, rows };
}
